import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Eye, ArrowUp, ArrowDown, ClipboardCheck, Check, X, Clock, Wrench } from 'lucide-react';

const API_BASE_URL = `${process.env.REACT_APP_API_URL.replace(/\/$/, '')}/api`;

const ECCWorkPermitTab = ({ activeSubTab }) => {
  const navigate = useNavigate();

  // Search states
  const [pendingSearch, setPendingSearch] = useState('');
  const [approvedSearch, setApprovedSearch] = useState('');
  const [rejectedSearch, setRejectedSearch] = useState('');
  const [expiredSearch, setExpiredSearch] = useState('');
  const [closedSearch, setClosedSearch] = useState('');

  // Dane dla różnych statusów
  const [pendingWorkpermits, setPendingWorkpermits] = useState([]);
  const [approvedWorkpermits, setApprovedWorkpermits] = useState([]);
  const [rejectedWorkpermits, setRejectedWorkpermits] = useState([]);
  const [expiredWorkpermits, setExpiredWorkpermits] = useState([]);
  const [closedWorkpermits, setClosedWorkpermits] = useState([]);

  // Stany ładowania i paginacji
  const [loading, setLoading] = useState({ pending: false, approved: false, rejected: false, expired: false, closed: false });
  const [currentPage, setCurrentPage] = useState({ pending: 1, approved: 1, rejected: 1, expired: 1, closed: 1 });
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Sortowanie - domyślnie po dateRange desc (najnowsze na górze)
  const [sortConfig, setSortConfig] = useState({
    pending: { key: 'dateRange', direction: 'desc' },
    approved: { key: 'dateRange', direction: 'desc' },
    rejected: { key: 'dateRange', direction: 'desc' },
    expired: { key: 'dateRange', direction: 'desc' },
    closed: { key: 'dateRange', direction: 'desc' },
  });

  // Stałe kolumny dla każdej zakładki
  const pendingColumns = ['workpermit', 'turbine', 'location', 'requester', 'company', 'submissionTime', 'dateRange', 'workScope'];
  const approvedColumns = ['workpermit', 'turbine', 'location', 'requester', 'company', 'approvalTime', 'dateRange', 'workScope'];
  const rejectedColumns = ['workpermit', 'turbine', 'location', 'requester', 'company', 'rejectedTime', 'dateRange', 'workScope'];
  const expiredColumns = ['workpermit', 'turbine', 'location', 'requester', 'company', 'expiredTime', 'dateRange', 'workScope'];
  const closedColumns = ['workpermit', 'turbine', 'location', 'requester', 'company', 'closedTime', 'dateRange', 'workScope'];

  // Określanie widoczności kart
  const visibleCards = {
    pending: activeSubTab === 'workpermits-pending',
    approved: activeSubTab === 'workpermits-approved',
    rejected: activeSubTab === 'workpermits-rejected',
    expired: activeSubTab === 'workpermits-expired',
    closed: activeSubTab === 'workpermits-closed',
  };

  // Pobieranie danych
  const fetchWorkpermitsByStatus = async (token, status, setData, loadingKey) => {
    try {
      setLoading((prev) => ({ ...prev, [loadingKey]: true }));
      const response = await axios.get(`${API_BASE_URL}/workpermit/`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { status },
      });
      // Logowanie pełnej odpowiedzi API
      console.log(`Odpowiedź API dla statusu ${status}:`, {
        status: response.status,
        data: response.data,
      });
      // Zabezpieczenie: ustaw pustą tablicę, jeśli response.data nie jest tablicą
      setData(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      // Logowanie szczegółów błędu
      console.error(`Błąd podczas pobierania workpermits (${status}):`, {
        message: error.message,
        status: error.response?.status,
        data: error.response?.data,
        headers: error.response?.headers,
      });
      setData([]);
    } finally {
      setLoading((prev) => ({ ...prev, [loadingKey]: false }));
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (!token) return;

    if (visibleCards.pending) fetchWorkpermitsByStatus(token, 'signed', setPendingWorkpermits, 'pending');
    if (visibleCards.approved) fetchWorkpermitsByStatus(token, 'approved', setApprovedWorkpermits, 'approved');
    if (visibleCards.rejected) fetchWorkpermitsByStatus(token, 'rejected', setRejectedWorkpermits, 'rejected');
    if (visibleCards.expired) fetchWorkpermitsByStatus(token, 'expired', setExpiredWorkpermits, 'expired');
    if (visibleCards.closed) fetchWorkpermitsByStatus(token, 'closed', setClosedWorkpermits, 'closed');
  }, [activeSubTab]);

  // Reset to first page when items per page changes or when filtered results change
  useEffect(() => {
    const activeWorkpermits = visibleCards.pending ? pendingWorkpermits :
                             visibleCards.approved ? approvedWorkpermits :
                             visibleCards.rejected ? rejectedWorkpermits :
                             visibleCards.expired ? expiredWorkpermits :
                             closedWorkpermits;
    const calculatedTotalPages = Math.ceil(activeWorkpermits.length / itemsPerPage) || 1;
    const currentKey = visibleCards.pending ? 'pending' :
                      visibleCards.approved ? 'approved' :
                      visibleCards.rejected ? 'rejected' :
                      visibleCards.expired ? 'expired' :
                      'closed';
    if (currentPage[currentKey] > calculatedTotalPages && calculatedTotalPages > 0) {
      handlePageChange(currentKey, 1);
    }
  }, [itemsPerPage, pendingWorkpermits.length, approvedWorkpermits.length, rejectedWorkpermits.length, expiredWorkpermits.length, closedWorkpermits.length, activeSubTab]);

  const formatDateTime = (dateTime) => {
    if (!dateTime) return 'N/A';
    return new Date(dateTime).toLocaleString('pl-PL', {
      year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit',
    });
  };

  const workpermitColumnDefinitions = {
    workpermit: {
      label: 'Workpermit',
      render: (item) => <span className="font-semibold text-graphite-900">{item.work_permit_number || 'N/A'}</span>
    },
    turbine: {
      label: 'Turbine',
      render: (item) => <div className="text-graphite-600">{item.turbine_data?.turbine_number || 'N/A'}</div>
    },
    location: {
      label: 'Location',
      render: (item) => <div className="text-graphite-600">{item.turbine_data?.location || 'N/A'}</div>
    },
    submissionTime: {
      label: 'Submitted',
      render: (item) => <div className="text-graphite-600">{formatDateTime(item.sign_date)}</div>
    },
    approvalTime: {
      label: 'Approved',
      render: (item) => <div className="text-graphite-600">{formatDateTime(item.approve_date)}</div>
    },
    rejectedTime: {
      label: 'Rejected',
      render: (item) => <div className="text-graphite-600">{formatDateTime(item.rejected_date)}</div>
    },
    expiredTime: {
      label: 'Expired',
      render: (item) => <div className="text-graphite-600">{formatDateTime(item.expired_date)}</div>
    },
    closedTime: {
      label: 'Closed',
      render: (item) => <div className="text-graphite-600">{formatDateTime(item.closed_date)}</div>
    },
    company: {
      label: 'Company',
      render: (item) => <div className="text-graphite-600 font-medium">{item.company_name || 'N/A'}</div>
    },
    dateRange: {
      label: 'Date Range',
      render: (item) => (
        <div className="text-graphite-600 text-xs">
          <span className="text-graphite-700">{formatDateTime(item.planned_date_start)}</span><br />
          <span className="text-graphite-500">to {formatDateTime(item.planned_date_end)}</span>
        </div>
      ),
    },
    requester: {
      label: 'Requester',
      render: (item) => {
        if (!item.team_data || !item.team_data.team_leader || !Array.isArray(item.team_data.users)) {
          return <span className="text-graphite-400 italic">N/A</span>;
        }
        const teamLeaderId = item.team_data.team_leader;
        const teamLeader = item.team_data.users.find(user => user.id === teamLeaderId);
        return teamLeader ? (
          <div className="text-graphite-700 font-medium">{`${teamLeader.first_name || ''} ${teamLeader.last_name || ''}`.trim() || 'N/A'}</div>
        ) : (
          <span className="text-graphite-400 italic">N/A</span>
        );
      },
    },
    workScope: {
      label: 'Work Scope',
      render: (item) => <div className="text-graphite-600">{item.work_scope_data?.name || 'N/A'}</div>,
    },
  };

  const getPageTitle = () => {
    switch (activeSubTab) {
      case 'workpermits-pending':
        return { title: 'Pending Work Permits', Icon: ClipboardCheck };
      case 'workpermits-approved':
        return { title: 'Approved Work Permits', Icon: Check };
      case 'workpermits-rejected':
        return { title: 'Rejected Work Permits', Icon: X };
      case 'workpermits-expired':
        return { title: 'Expired Work Permits', Icon: Clock };
      case 'workpermits-closed':
        return { title: 'Closed Work Permits', Icon: ClipboardCheck };
      default:
        return { title: 'Work Permits', Icon: ClipboardCheck };
    }
  };

  const filterWorkpermits = (workpermits, searchTerm) => {
    if (!searchTerm) return workpermits;
    const lowerSearch = searchTerm.toLowerCase();
    return workpermits.filter((item) => {
      let requesterMatch = false;
      if (item.team_data && item.team_data.team_leader && Array.isArray(item.team_data.users)) {
        const teamLeaderId = item.team_data.team_leader;
        const teamLeader = item.team_data.users.find(user => user.id === teamLeaderId);
        if (teamLeader) {
          const fullName = `${teamLeader.first_name || ''} ${teamLeader.last_name || ''}`.trim().toLowerCase();
          requesterMatch = fullName.includes(lowerSearch);
        }
      }
      return (
        (item.work_permit_number || '').toLowerCase().includes(lowerSearch) ||
        (item.company_name || '').toLowerCase().includes(lowerSearch) ||
        (item.turbine_data?.turbine_number || '').toLowerCase().includes(lowerSearch) ||
        (item.turbine_data?.location || '').toLowerCase().includes(lowerSearch) ||
        (item.work_scope_data?.name || '').toLowerCase().includes(lowerSearch) ||
        requesterMatch
      );
    });
  };

  const sortWorkpermits = (workpermits, view) => {
    const { key, direction } = sortConfig[view];
    const sorted = [...workpermits];

    sorted.sort((a, b) => {
      if (key === 'workpermit') {
        const aValue = a.work_permit_number || '';
        const bValue = b.work_permit_number || '';
        if (!aValue && !bValue) return 0;
        if (!aValue) return 1;
        if (!bValue) return -1;
        return direction === 'asc' ? 
          String(aValue).localeCompare(String(bValue)) : 
          String(bValue).localeCompare(String(aValue));
      } else if (key === 'company' || key === 'turbine' || key === 'location' || key === 'workScope') {
        const aValue = key === 'turbine' ? (a.turbine_data?.turbine_number || '') :
                       key === 'location' ? (a.turbine_data?.location || '') :
                       key === 'workScope' ? (a.work_scope_data?.name || '') :
                       (a[key] || '');
        const bValue = key === 'turbine' ? (b.turbine_data?.turbine_number || '') :
                       key === 'location' ? (b.turbine_data?.location || '') :
                       key === 'workScope' ? (b.work_scope_data?.name || '') :
                       (b[key] || '');
        if (!aValue && !bValue) return 0;
        if (!aValue) return 1;
        if (!bValue) return -1;
        return direction === 'asc' ? 
          String(aValue).localeCompare(String(bValue)) : 
          String(bValue).localeCompare(String(aValue));
      } else if (key === 'submissionTime' || key === 'approvalTime' || key === 'rejectedTime' || key === 'expiredTime' || key === 'closedTime') {
        const aDateRaw = a[key === 'submissionTime' ? 'sign_date' : 
                           key === 'approvalTime' ? 'approve_date' : 
                           key === 'rejectedTime' ? 'rejected_date' : 
                           key === 'expiredTime' ? 'expired_date' : 
                           'closed_date'];
        const bDateRaw = b[key === 'submissionTime' ? 'sign_date' : 
                           key === 'approvalTime' ? 'approve_date' : 
                           key === 'rejectedTime' ? 'rejected_date' : 
                           key === 'expiredTime' ? 'expired_date' : 
                           'closed_date'];
        const aDate = aDateRaw ? new Date(aDateRaw) : null;
        const bDate = bDateRaw ? new Date(bDateRaw) : null;
        if (!aDate && !bDate) return 0;
        if (!aDate) return 1;
        if (!bDate) return -1;
        return direction === 'asc' ? aDate - bDate : bDate - aDate;
      } else if (key === 'dateRange') {
        const aDateEndRaw = a.planned_date_end;
        const bDateEndRaw = b.planned_date_end;
        const aDateEnd = aDateEndRaw ? new Date(aDateEndRaw) : null;
        const bDateEnd = bDateEndRaw ? new Date(bDateEndRaw) : null;
        if (!aDateEnd && !bDateEnd) return 0;
        if (!aDateEnd) return 1;
        if (!bDateEnd) return -1;
        return direction === 'asc' ? aDateEnd - bDateEnd : bDateEnd - aDateEnd;
      } else if (key === 'requester') {
        const aLeader = a.team_data?.users?.find(u => u.id === a.team_data?.team_leader);
        const bLeader = b.team_data?.users?.find(u => u.id === b.team_data?.team_leader);
        const aValue = aLeader ? `${aLeader.first_name || ''} ${aLeader.last_name || ''}`.trim() : '';
        const bValue = bLeader ? `${bLeader.first_name || ''} ${bLeader.last_name || ''}`.trim() : '';
        if (!aValue && !bValue) return 0;
        if (!aValue) return 1;
        if (!bValue) return -1;
        return direction === 'asc' ? 
          String(aValue).localeCompare(String(bValue)) : 
          String(bValue).localeCompare(String(aValue));
      }
      return 0;
    });

    return sorted;
  };

  const paginate = (data, page) => {
    const startIndex = (page - 1) * itemsPerPage;
    return data.slice(startIndex, startIndex + itemsPerPage);
  };

  const getTotalPages = (data) => Math.ceil(data.length / itemsPerPage);

  const handlePageChange = (loadingKey, page) => {
    setCurrentPage((prev) => ({ ...prev, [loadingKey]: page }));
  };

  const handleSort = (view, key) => {
    setSortConfig((prev) => {
      const currentDirection = prev[view].key === key && prev[view].direction === 'asc' ? 'desc' : 'asc';
      return { ...prev, [view]: { key, direction: currentDirection } };
    });
    handlePageChange(view, 1);
  };

  const renderSortIndicator = (view, columnKey) => {
    const { key, direction } = sortConfig[view];
    if (key !== columnKey) return null;
    return direction === 'asc' ? (
      <ArrowUp className="h-3 w-3 ml-1" strokeWidth={1.5} />
    ) : (
      <ArrowDown className="h-3 w-3 ml-1" strokeWidth={1.5} />
    );
  };

  const handleViewWorkpermit = (workpermit) => {
    navigate(`/ecc/approve?workpermit_id=${workpermit.id}`);
  };

  const renderTable = (workpermits, loadingKey, pageTitle, columns) => {
    const { title, Icon } = pageTitle; // Destrukturyzacja obiektu pageTitle
    const filteredWorkpermits = filterWorkpermits(workpermits, 
      loadingKey === 'pending' ? pendingSearch :
      loadingKey === 'approved' ? approvedSearch :
      loadingKey === 'rejected' ? rejectedSearch :
      loadingKey === 'expired' ? expiredSearch :
      closedSearch
    );
    const sortedWorkpermits = sortWorkpermits(filteredWorkpermits, loadingKey);
    const totalPages = getTotalPages(sortedWorkpermits);
    const current = currentPage[loadingKey];
    const paginatedWorkpermits = paginate(sortedWorkpermits, current);
    const isPending = loadingKey === 'pending';

    const getVisiblePages = () => {
      const maxVisiblePages = 5; // Bieżąca strona + 2 do przodu + 2 do tyłu
      const pages = [];
      const startPage = Math.max(1, current - 2);
      const endPage = Math.min(totalPages, current + 2);

      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }
      return pages;
    };

    return (
      <div className="h-screen flex flex-col">
        <div className="flex-shrink-0 p-4">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center">
            <h1 className="flex items-center text-2xl font-bold text-graphite-900">
              <Icon className="h-6 w-6 mr-3 text-brand-500" />
              {title}
            </h1>
          </div>
        </div>

        <div className="flex-1 px-4 pb-4 overflow-hidden">
          <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-steel-200 h-full flex flex-col">
            <div className="p-4 flex-shrink-0 border-b-2 border-brand-500 bg-steel-50">
              <div className="flex flex-col md:flex-row items-center gap-4">
                <div className="relative flex-1 w-full">
                  <input
                    placeholder="Search by permit, company, turbine, location, scope, requester..."
                    className="w-full pl-10 pr-4 py-2.5 border border-steel-300 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 text-graphite-900 placeholder:text-graphite-400"
                    type="text"
                    value={
                      loadingKey === 'pending' ? pendingSearch :
                      loadingKey === 'approved' ? approvedSearch :
                      loadingKey === 'rejected' ? rejectedSearch :
                      loadingKey === 'expired' ? expiredSearch :
                      closedSearch
                    }
                    onChange={(e) => {
                      if (loadingKey === 'pending') setPendingSearch(e.target.value);
                      else if (loadingKey === 'approved') setApprovedSearch(e.target.value);
                      else if (loadingKey === 'rejected') setRejectedSearch(e.target.value);
                      else if (loadingKey === 'expired') setExpiredSearch(e.target.value);
                      else setClosedSearch(e.target.value);
                      handlePageChange(loadingKey, 1);
                    }}
                  />
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="lucide lucide-search absolute left-3 top-1/2 transform -translate-y-1/2 text-graphite-400 h-5 w-5"
                  >
                    <circle cx="11" cy="11" r="8" />
                    <path d="m21 21-4.3-4.3" />
                  </svg>
                </div>
              </div>
            </div>
            
            {/* Table container */}
            <div className="flex-1 overflow-hidden flex flex-col">
              <div className="flex-1 overflow-y-auto">
                <table className="min-w-full divide-y divide-steel-200">
                  <thead className="bg-steel-100 sticky top-0 z-10">
                    <tr>
                      {columns.map((key) => (
                        <th
                          key={key}
                          className="px-4 py-3 text-left text-xs font-semibold text-graphite-700 uppercase tracking-wider"
                        >
                          <button
                            className="flex items-center gap-1 hover:text-brand-500 transition-colors"
                            onClick={() => handleSort(loadingKey, key)}
                          >
                            {workpermitColumnDefinitions[key].label}
                            {renderSortIndicator(loadingKey, key)}
                          </button>
                        </th>
                      ))}
                      <th className="px-4 py-3 text-left text-xs font-semibold text-graphite-700 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-steel-100">
                    {loading[loadingKey] ? (
                      <tr>
                        <td colSpan={columns.length + 1} className="px-4 py-8 text-center text-graphite-500">
                          <div className="flex items-center justify-center gap-2">
                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-brand-500"></div>
                            Loading {title.toLowerCase()}...
                          </div>
                        </td>
                      </tr>
                    ) : paginatedWorkpermits.length === 0 ? (
                      <tr>
                        <td colSpan={columns.length + 1} className="px-4 py-8 text-center text-graphite-500">
                          No {title.toLowerCase()} available.
                        </td>
                      </tr>
                    ) : (
                      paginatedWorkpermits.map((workpermit) => (
                        <tr
                          key={workpermit.id}
                          className="hover:bg-brand-50/30 transition-colors cursor-pointer"
                          onDoubleClick={() => handleViewWorkpermit(workpermit)}
                        >
                          {columns.map((key) => (
                            <td key={key} className="px-4 py-3 whitespace-nowrap text-sm">
                              {workpermitColumnDefinitions[key].render(workpermit)}
                            </td>
                          ))}
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-graphite-500">
                            {isPending ? (
                              <button
                                className="px-4 py-1.5 bg-brand-500 text-white text-xs font-medium rounded-full hover:bg-brand-600 transition-colors"
                                onClick={() => handleViewWorkpermit(workpermit)}
                              >
                                Verify
                              </button>
                            ) : (
                              <div className="flex items-center gap-1">
                                <button
                                  className="p-1.5 bg-steel-100 text-graphite-700 rounded-full hover:bg-steel-200 transition-colors"
                                  aria-label="View"
                                  title="View Details"
                                  onClick={() => handleViewWorkpermit(workpermit)}
                                >
                                  <Eye className="h-4 w-4" />
                                </button>
                                <button
                                  className={`p-1.5 rounded-full transition-colors ${
                                    workpermit.isolation_flow_status?.has_submission
                                      ? 'bg-brand-50 text-brand-500 hover:bg-brand-100'
                                      : 'bg-steel-100 text-steel-400 hover:bg-steel-200'
                                  }`}
                                  aria-label="Isolation Flow"
                                  title={workpermit.isolation_flow_status?.has_submission
                                    ? 'View Isolation Flow'
                                    : 'No Isolation Flow submission'}
                                  onClick={() => navigate(`/ecc/approve?workpermit_id=${workpermit.id}&tab=isolation`)}
                                >
                                  <Wrench className="h-4 w-4" />
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              
              {/* Pagination */}
              <div className="flex items-center justify-between px-4 py-4 border-t border-steel-200 bg-steel-50">
                <div className="flex items-center gap-4">
                  <div className="text-sm text-graphite-600">
                    Showing <span className="font-medium text-graphite-900">{(current - 1) * itemsPerPage + 1}-{Math.min(current * itemsPerPage, sortedWorkpermits.length)}</span> of <span className="font-medium text-graphite-900">{sortedWorkpermits.length}</span> items
                  </div>
                  <select
                    value={itemsPerPage}
                    onChange={(e) => {
                      setItemsPerPage(Number(e.target.value));
                      handlePageChange(loadingKey, 1);
                    }}
                    className="text-sm px-4 py-2 border border-steel-300 rounded-xl bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 min-w-[140px]"
                  >
                    <option value={5}>5 per page</option>
                    <option value={10}>10 per page</option>
                    <option value={25}>25 per page</option>
                    <option value={50}>50 per page</option>
                    <option value={100}>100 per page</option>
                  </select>
                </div>
                <div className="flex items-center gap-2">
                  {current > 1 && (
                    <button
                      className="px-4 py-2 border border-steel-300 text-graphite-700 rounded-xl bg-white hover:bg-steel-50 hover:border-brand-300 flex items-center justify-center transition-colors"
                      onClick={() => handlePageChange(loadingKey, current - 1)}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="lucide lucide-chevron-left h-4 w-4 mr-1"
                      >
                        <path d="m15 18-6-6 6-6" />
                      </svg>
                      Previous
                    </button>
                  )}
                  {getVisiblePages().map((page) => (
                    <button
                      key={page}
                      className={`w-10 h-10 border ${
                        page === current
                          ? 'border-brand-500 bg-brand-500 text-white font-medium shadow-sm'
                          : 'border-steel-300 text-graphite-700 bg-white hover:bg-steel-50 hover:border-brand-300'
                      } rounded-xl flex items-center justify-center transition-colors`}
                      onClick={() => handlePageChange(loadingKey, page)}
                    >
                      {page}
                    </button>
                  ))}
                  {current < totalPages && (
                    <button
                      className="px-4 py-2 border border-steel-300 text-graphite-700 rounded-xl bg-white hover:bg-steel-50 hover:border-brand-300 flex items-center justify-center transition-colors"
                      onClick={() => handlePageChange(loadingKey, current + 1)}
                    >
                      Next
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="lucide lucide-chevron-right h-4 w-4 ml-1"
                      >
                        <path d="m9 18 6-6-6-6" />
                      </svg>
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="h-screen flex flex-col">
      {visibleCards.pending && renderTable(pendingWorkpermits, 'pending', getPageTitle(), pendingColumns)}
      {visibleCards.approved && renderTable(approvedWorkpermits, 'approved', getPageTitle(), approvedColumns)}
      {visibleCards.rejected && renderTable(rejectedWorkpermits, 'rejected', getPageTitle(), rejectedColumns)}
      {visibleCards.expired && renderTable(expiredWorkpermits, 'expired', getPageTitle(), expiredColumns)}
      {visibleCards.closed && renderTable(closedWorkpermits, 'closed', getPageTitle(), closedColumns)}
    </div>
  );
};

export default ECCWorkPermitTab;